import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Tabs, Tab, Stepper, Step, StepLabel, Button,
  Grid, TextField, Chip, Table, TableBody, TableCell, TableHead, TableRow,
  List, ListItem, ListItemIcon, ListItemText, MenuItem, ListSubheader, Checkbox, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Autocomplete,
  TableSortLabel,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileBrowser from '../components/common/FileBrowser';
import ProjectFolderLinks from '../components/common/ProjectFolderLinks';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ConceptualEngineering from './ConceptualEngineering';
import BasicEngineering from './BasicEngineering';
import DetailEngineering from './DetailEngineering';
import Offers from './Offers';
import ProjectPlanning from './ProjectPlanning';
import ManufacturingOrders from './ManufacturingOrders';
import Communications from './Communications';
import ProjectChat from '../components/common/ProjectChat';
import EditableTable from '../components/common/EditableTable';
import PaymentConditionsEditor from '../components/common/PaymentConditionsEditor';
import {
  projectsAPI, clientsAPI, suppliersAPI, engineeringAPI,
  projectClientConditionsAPI, projectSupplierConditionsAPI, configAPI, ordersAPI,
} from '../services/api';
import { CircularProgress, Alert } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

const PROJECT_PHASES = [
  { key: 'request', label: 'Peticion' },
  { key: 'conceptual', label: 'Ing. Conceptual' },
  { key: 'basic', label: 'Ing. Basica' },
  { key: 'offer', label: 'Oferta' },
  { key: 'approved', label: 'Oferta Aprobada' },
  { key: 'detail', label: 'Ing. Detalle' },
  { key: 'execution', label: 'Ejecucion' },
  { key: 'commissioning', label: 'Puesta en Marcha' },
];

const STATUS_TO_STEP = {
  opportunity: 0, conceptual: 1, offer_preparation: 2, offer_sent: 3,
  offer_approved: 4, in_progress: 5, commissioning: 6, completed: 7,
};

const defaultFolders = [
  '01_Ingenieria_Conceptual', '02_Ingenieria_Basica', '03_Oferta',
  '04_Ingenieria_Detalle', '05_Compras', '06_Fabricacion',
  '07_Montaje', '08_Puesta_en_Marcha', '09_Documentacion_Final', '10_Economico',
];

const STATUS_LABELS = {
  opportunity: 'Oportunidad', conceptual: 'Ing. Conceptual',
  offer_preparation: 'Preparando Oferta', offer_sent: 'Oferta Enviada',
  offer_approved: 'Oferta Aprobada', in_progress: 'Ing. Detalle',
  commissioning: 'Ejecucion', completed: 'Completado',
};

const STATUS_COLORS = {
  opportunity: 'default', conceptual: 'info', offer_preparation: 'warning',
  offer_sent: 'secondary', offer_approved: 'success', in_progress: 'primary',
  commissioning: 'warning', completed: 'success',
};

const clientCondColumns = [
  { field: 'description', header: 'Descripcion', width: '25%' },
  { field: 'percentage', header: '%', type: 'number' },
  { field: 'milestone', header: 'Hito' },
  { field: 'delay_days', header: 'Dias', type: 'number' },
  { field: 'delay_from', header: 'Desde' },
  { field: 'amount', header: 'Importe (EUR)', type: 'number' },
];

const clientCondFields = [
  { field: 'description', label: 'Descripcion', required: true, fullWidth: true },
  { field: 'percentage', label: '% del total', type: 'number', required: true },
  { field: 'milestone', label: 'Hito', type: 'select', options: [
    { value: 'pedido', label: 'Pedido' }, { value: 'entrega', label: 'Entrega' },
    { value: 'factura', label: 'Factura' }, { value: 'puesta_en_marcha', label: 'Puesta en marcha' },
  ]},
  { field: 'delay_days', label: 'Dias de retraso', type: 'number' },
  { field: 'delay_from', label: 'Contar desde', type: 'select', options: [
    { value: 'milestone', label: 'Fecha del hito' }, { value: 'invoice_date', label: 'Fecha factura' },
  ]},
  { field: 'amount', label: 'Importe (EUR)', type: 'number' },
];


function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  try { return new Date(dateStr).toISOString().slice(0, 10); } catch { return ''; }
}

// Format a number as EUR with Spanish locale (thousands dot, decimal comma)
function fmtEUR(val, maxDecimals = 2) {
  const n = parseFloat(val) || 0;
  return new Intl.NumberFormat('es-ES', {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimals,
  }).format(n);
}

function fmtDate(dateStr) {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return '—'; }
}

// Group supplier offers by manufacturing order (loose offers last).
function groupOffersByManufacturing(offers) {
  const groups = new Map();
  offers.forEach((o) => {
    const key = o.manufacturing_order_id || 'loose';
    if (!groups.has(key)) {
      groups.set(key, { key, code: o.manufacturing_order_code || null, offers: [] });
    }
    groups.get(key).offers.push(o);
  });
  const arr = Array.from(groups.values());
  // Manufacturing orders first (by code), loose offers last
  arr.sort((a, b) => {
    if (!a.code && b.code) return 1;
    if (a.code && !b.code) return -1;
    return String(a.code || '').localeCompare(String(b.code || ''), 'es', { numeric: true });
  });
  return arr;
}

function toDateTime(dateStr) {
  if (!dateStr) return null;
  return `${dateStr}T00:00:00`;
}

export default function ProjectFlow() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [project, setProject] = useState(null);
  const [client, setClient] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [clientConds, setClientConds] = useState([]);
  const [supplierConds, setSupplierConds] = useState([]);
  const [scSortField, setScSortField] = useState('supplier_name');
  const [scSortDir, setScSortDir] = useState('asc');
  const [addSupplierOpen, setAddSupplierOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [addingSupplier, setAddingSupplier] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmAdvanceOpen, setConfirmAdvanceOpen] = useState(false);
  const [creatingFolders, setCreatingFolders] = useState(false);
  const [folderError, setFolderError] = useState(null);
  const [folderSuccess, setFolderSuccess] = useState(false);
  const [formData, setFormData] = useState({});
  const [projectTypes, setProjectTypes] = useState([]);
  const [clients, setClients] = useState([]);

  const saveGeneralRef = React.useRef(null);
  React.useEffect(() => { saveGeneralRef.current = formData; }, [formData]);

  // --- Pedidos state ---
  const [orders, setOrders] = useState([]);
  const [offersWithoutOrder, setOffersWithoutOrder] = useState([]);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [orderEditId, setOrderEditId] = useState(null);
  const [orderForm, setOrderForm] = useState({});
  const [orderSaving, setOrderSaving] = useState(false);
  const [orderPaymentConds, setOrderPaymentConds] = useState([]);
  const [deleteConfirmSupplierCond, setDeleteConfirmSupplierCond] = useState(null);
  const [deleteConfirmOrder, setDeleteConfirmOrder] = useState(null);
  const [pdfLangDialog, setPdfLangDialog] = useState(null); // order id or null

  useEffect(() => {
    configAPI.listProjectTypes().then((resp) => setProjectTypes(resp.data)).catch(() => {});
    clientsAPI.list().then((resp) => setClients(resp.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const loadProject = async () => {
      try {
        setLoading(true);
        const [projectResp, suppliersResp] = await Promise.all([
          projectsAPI.get(id),
          suppliersAPI.list(),
        ]);
        const pd = projectResp.data;
        setProject(pd);
        setSuppliers(suppliersResp.data);
        setFormData({
          name: pd.name || '', project_type: pd.project_type || '',
          client_id: pd.client_id || '',
          production_capacity: pd.production_capacity || '',
          product_description: pd.product_description || '',
          request_date: formatDate(pd.request_date),
          offer_date: formatDate(pd.offer_date),
          approval_date: formatDate(pd.approval_date),
          approved_amount: pd.approved_amount != null ? pd.approved_amount : '',
          estimated_end_date: formatDate(pd.estimated_end_date),
          actual_end_date: formatDate(pd.actual_end_date),
          delivery_weeks: pd.delivery_weeks || '',
          notes: pd.notes || ''
        });
        if (pd.client_id) {
          const clientResp = await clientsAPI.get(pd.client_id);
          setClient(clientResp.data);
        }
        try {
          const [ccResp, scResp] = await Promise.all([
            projectClientConditionsAPI.list(id),
            projectSupplierConditionsAPI.list(id),
          ]);
          setClientConds(ccResp.data);
          setSupplierConds(scResp.data);
        } catch { setClientConds([]); setSupplierConds([]); }
        setError(null);
      } catch (err) {
        console.error('Error loading project:', err);
        setError('Error al cargar el proyecto.');
      } finally { setLoading(false); }
    };
    if (id) loadProject();
  }, [id]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}><CircularProgress /></Box>;
  if (error || !project) return <Box sx={{ p: 3 }}><Alert severity="error" sx={{ mb: 2 }}>{error || 'Proyecto no encontrado'}</Alert><Button variant="outlined" onClick={() => navigate('/projects')}>Volver</Button></Box>;

  const statusKey = (project.status || '').toLowerCase();
  const currentStep = STATUS_TO_STEP[statusKey] || 0;

  const handleAdvancePhase = () => {
    if (statusKey === 'offer_approved') {
      setConfirmAdvanceOpen(true);
    } else {
      doAdvancePhase();
    }
  };

  const doAdvancePhase = async () => {
    setConfirmAdvanceOpen(false);
    try {
      await engineeringAPI.advancePhase(project.id);
      // Reload full project to get updated code and status
      const projectResp = await projectsAPI.get(project.id);
      setProject(projectResp.data);
    } catch (err) {
      console.error('Error:', err);
      alert(err.response?.data?.detail || 'Error al avanzar fase');
    }
  };

  const handleRollbackPhase = async () => {
    try {
      const resp = await engineeringAPI.rollbackPhase(project.id);
      setProject({ ...project, status: resp.data.status });
    } catch (err) {
      console.error('Error:', err);
      alert(err.response?.data?.detail || 'Error al retroceder fase');
    }
  };

  const handleSaveGeneral = async (dataOverride) => {
    const data = dataOverride || formData;
    try {
      setSaving(true);
      await projectsAPI.update(project.id, {
        name: data.name,
        project_type: data.project_type,
        client_id: data.client_id || null,
        production_capacity: data.production_capacity,
        product_description: data.product_description,
        request_date: toDateTime(data.request_date),
        offer_date: toDateTime(data.offer_date),
        approval_date: toDateTime(data.approval_date),
        approved_amount: data.approved_amount !== '' ? parseFloat(data.approved_amount) : null,
        estimated_end_date: toDateTime(data.estimated_end_date),
        actual_end_date: toDateTime(data.actual_end_date),
        delivery_weeks: data.delivery_weeks ? parseInt(data.delivery_weeks) : null,
        notes: data.notes,
      });
      const resp = await projectsAPI.get(project.id);
      setProject(resp.data);
      if (data.client_id) {
        const selectedClient = clients.find((c) => c.id === data.client_id);
        if (selectedClient) setClient(selectedClient);
      }
    } catch (err) { console.error('Error:', err); } finally { setSaving(false); }
  };
  const handleBlurGeneral = () => {
    if (saveGeneralRef.current) handleSaveGeneral(saveGeneralRef.current);
  };

  const handleFormChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleCreateFolders = async () => {
    setCreatingFolders(true);
    setFolderError(null);
    setFolderSuccess(false);
    try {
      const r = await projectsAPI.createFolders(project.id);
      setProject(r.data);
      setFolderSuccess(true);
    } catch (err) {
      setFolderError(err.response?.data?.detail || 'Error al crear las carpetas');
    } finally {
      setCreatingFolders(false);
    }
  };

  const reloadClientConds = async () => { const r = await projectClientConditionsAPI.list(project.id); setClientConds(r.data); };
  const reloadSupplierConds = async () => { const r = await projectSupplierConditionsAPI.list(project.id); setSupplierConds(r.data); };
  const reloadOrders = async () => {
    const [ordResp, offResp] = await Promise.all([
      ordersAPI.list(project.id),
      ordersAPI.offersWithoutOrder(project.id),
    ]);
    setOrders(ordResp.data);
    setOffersWithoutOrder(offResp.data);
  };

  const clientCondHandlers = {
    onAdd: async (data) => { await projectClientConditionsAPI.create(project.id, data); await reloadClientConds(); },
    onUpdate: async (itemId, data) => { await projectClientConditionsAPI.update(itemId, data); await reloadClientConds(); },
    onDelete: async (itemId) => { await projectClientConditionsAPI.delete(itemId); await reloadClientConds(); },
  };

  const handleCopyClientConditions = async () => {
    try { await engineeringAPI.copyClientConditions(project.id); await reloadClientConds(); }
    catch (err) { alert(err.response?.data?.detail || 'Error al copiar condiciones'); }
  };

  const handleCopySupplierConditions = async (supplierId) => {
    try { await engineeringAPI.copySupplierConditions(project.id, supplierId); await reloadSupplierConds(); }
    catch (err) { alert(err.response?.data?.detail || 'Error al copiar condiciones'); }
  };

  const handleAddSupplierConditions = async () => {
    if (!selectedSupplier) return;
    try {
      setAddingSupplier(true);
      await engineeringAPI.copySupplierConditions(project.id, selectedSupplier.id);
      await reloadSupplierConds();
      setAddSupplierOpen(false);
      setSelectedSupplier(null);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al copiar condiciones del proveedor');
    } finally {
      setAddingSupplier(false);
    }
  };

  const handleDeleteSupplierCond = async (condId) => {
    try {
      await projectSupplierConditionsAPI.delete(condId);
      await reloadSupplierConds();
    } catch (err) { console.error('Error deleting supplier condition:', err); }
  };

  const handleTogglePaid = async (cond) => {
    try {
      await projectSupplierConditionsAPI.update(cond.id, { is_paid: cond.is_paid ? 0 : 1 });
      await reloadSupplierConds();
    } catch (err) { console.error('Error updating paid status:', err); }
  };

  const handleRefreshSuppliers = async () => {
    try {
      const result = await engineeringAPI.refreshSupplierConditions(project.id);
      await reloadSupplierConds();
      const { added, not_found } = result.data;
      if (added && added.length > 0) {
        alert(`Proveedores añadidos: ${added.join(', ')}`);
      } else if (not_found && not_found.length > 0) {
        alert(`No se encontraron en la BD: ${not_found.join(', ')}. El resto ya tenía condiciones.`);
      } else {
        alert('Todos los proveedores de los equipos ya tienen condiciones asignadas.');
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al refrescar proveedores');
    }
  };

  // --- Order handlers ---
  const DEFAULT_LEGAL_TERMS = `1. El presente pedido queda sujeto a las condiciones técnicas y económicas acordadas entre las partes.
2. El proveedor deberá confirmar la recepción del pedido en un plazo máximo de 5 días laborables.
3. Los plazos de entrega indicados son vinculantes. Cualquier retraso deberá ser comunicado con suficiente antelación.
4. La mercancía será inspeccionada a su recepción. En caso de no conformidad, el proveedor deberá hacerse cargo de los costes de devolución y sustitución.
5. Las facturas deben hacer referencia al número de pedido indicado en el presente documento.
6. El pago se realizará según las condiciones indicadas en el presente pedido, contadas desde la fecha del hito acordado.
7. La propiedad intelectual de los diseños y especificaciones técnicas proporcionadas por el comprador permanece en todo momento en manos del comprador.`;

  const fetchSupplierPaymentConditions = async (supplierId) => {
    try {
      const resp = await suppliersAPI.listConditions(supplierId);
      const conds = resp.data || [];
      if (conds.length === 0) return '';
      return conds
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((c) => `${c.percentage}% - ${c.description}${c.delay_days ? ` (${c.delay_days} dias)` : ''}`)
        .join('\n');
    } catch { return ''; }
  };

  const openNewOrder = async (offer = null) => {
    setOrderEditId(null);
    setOrderPaymentConds([]);
    // Payment conditions text (legacy, used as fallback)
    let paymentConds = offer?.payment_conditions || '';
    if (!paymentConds && (offer?.supplier_id)) {
      paymentConds = await fetchSupplierPaymentConditions(offer.supplier_id);
    }
    const weeks = parseInt(offer?.delivery_weeks) || 0;
    const orderDate = new Date().toISOString().slice(0, 10);
    let deliveryDate = '';
    if (weeks > 0) {
      const d = new Date(orderDate);
      d.setDate(d.getDate() + weeks * 7);
      deliveryDate = d.toISOString().slice(0, 10);
    }
    setOrderForm({
      supplier_id: offer?.supplier_id || '',
      supplier_name: offer?.supplier_name || '',
      supplier_offer_id: offer?.id || '',
      amount: offer?.amount || '',
      delivery_weeks: offer?.delivery_weeks || '',
      delivery_date: deliveryDate,
      order_date: orderDate,
      payment_conditions: paymentConds,
      legal_terms: DEFAULT_LEGAL_TERMS,
      comments: offer?.notes || '',
      status: 'draft',
      manufacturing_order_id: offer?.manufacturing_order_id || null,
      manufacturing_order_code: offer?.manufacturing_order_code || null,
    });
    setOrderDialogOpen(true);
  };

  const loadOrderPaymentConds = async (orderId) => {
    try {
      const resp = await ordersAPI.listPaymentConditions(orderId);
      setOrderPaymentConds(resp.data);
    } catch { setOrderPaymentConds([]); }
  };

  const openEditOrder = (order) => {
    setOrderEditId(order.id);
    setOrderForm({
      supplier_id: order.supplier_id,
      supplier_name: order.supplier_name,
      supplier_offer_id: order.supplier_offer_id || '',
      amount: order.amount || '',
      delivery_weeks: order.delivery_weeks || '',
      delivery_date: formatDate(order.delivery_date),
      order_date: formatDate(order.order_date),
      payment_conditions: order.payment_conditions || '',
      legal_terms: order.legal_terms || DEFAULT_LEGAL_TERMS,
      comments: order.comments || '',
      status: order.status || 'draft',
      manufacturing_order_id: order.manufacturing_order_id || null,
      manufacturing_order_code: order.manufacturing_order_code || null,
    });
    loadOrderPaymentConds(order.id);
    setOrderDialogOpen(true);
  };

  const handleSaveOrder = async () => {
    if (!orderForm.supplier_id) { alert('Selecciona un proveedor'); return; }
    try {
      setOrderSaving(true);
      const payload = {
        supplier_id: orderForm.supplier_id,
        supplier_offer_id: orderForm.supplier_offer_id || null,
        manufacturing_order_id: orderForm.manufacturing_order_id || null,
        amount: parseFloat(orderForm.amount) || null,
        delivery_weeks: parseInt(orderForm.delivery_weeks) || null,
        delivery_date: orderForm.delivery_date ? `${orderForm.delivery_date}T00:00:00` : null,
        order_date: orderForm.order_date ? `${orderForm.order_date}T00:00:00` : null,
        payment_conditions: orderForm.payment_conditions || null,
        legal_terms: orderForm.legal_terms || null,
        comments: orderForm.comments || null,
        status: orderForm.status || 'draft',
      };
      if (orderEditId) {
        await ordersAPI.update(orderEditId, payload);
      } else {
        await ordersAPI.create(project.id, payload);
      }
      await reloadOrders();
      await reloadSupplierConds();
      setOrderDialogOpen(false);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al guardar el pedido');
    } finally {
      setOrderSaving(false);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    try {
      await ordersAPI.delete(orderId);
      await reloadOrders();
      await reloadSupplierConds();
    } catch (err) { alert(err.response?.data?.detail || 'Error al eliminar'); }
  };

  const handleDownloadOrderPdf = async (orderId, lang) => {
    try {
      const res = await ordersAPI.pdfBlob(orderId, lang);
      const blobUrl = URL.createObjectURL(res.data);
      window.open(blobUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al generar el PDF');
    } finally {
      setPdfLangDialog(null);
    }
  };

  const handleTabChange = (e, v) => {
    setActiveTab(v);
    if (v === 11 && project) reloadOrders();
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">{project.code} - {project.name}</Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <Chip label={STATUS_LABELS[statusKey]} color={STATUS_COLORS[statusKey]} />
            <Chip label={`Oferta: ${project.offer_code}`} variant="outlined" size="small" />
            <Typography variant="body2" color="textSecondary">
              Cliente: <strong>{client?.name || '...'}</strong> | Peticion: {formatDate(project.request_date) || 'N/A'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {isAdmin && statusKey !== 'opportunity' && (
            <Button variant="outlined" color="warning" onClick={handleRollbackPhase}>Retroceder Fase</Button>
          )}
          <Button variant="contained" onClick={handleAdvancePhase} disabled={statusKey === 'completed'}>Avanzar Fase</Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stepper activeStep={currentStep} alternativeLabel>
          {PROJECT_PHASES.map((phase) => <Step key={phase.key}><StepLabel>{phase.label}</StepLabel></Step>)}
        </Stepper>
      </Paper>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          <Tab label="Datos Generales" />
          <Tab label="Ing. Conceptual" />
          <Tab label="Ing. Basica" />
          <Tab label="Ofertas" />
          <Tab label="Ing. Detalle" />
          <Tab label="Fabricación" />
          <Tab label="Planning" />
          <Tab label="Documentos" />
          <Tab label="Cond. Cobro (Cliente)" />
          <Tab label="Cond. Pago (Proveedores)" />
          <Tab label="Flujo de Caja" />
          <Tab label="Pedidos" />
          <Tab label="Seguimiento" />
          <Tab label="Chat" />
        </Tabs>
      </Paper>

      {/* Tab 0: Datos generales */}
      <TabPanel value={activeTab} index={0}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Datos del Proyecto</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}><TextField fullWidth label="N. Proyecto" value={project.code} InputProps={{ readOnly: true }} /></Grid>
            <Grid item xs={12} sm={3}><TextField fullWidth label="N. Oferta" value={project.offer_code || ''} InputProps={{ readOnly: true }} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Nombre" name="name" value={formData.name} onChange={handleFormChange} onBlur={handleBlurGeneral} /></Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth select label="Tipo de proyecto" name="project_type" value={formData.project_type} onChange={(e) => { handleFormChange(e); setTimeout(() => handleSaveGeneral({ ...formData, project_type: e.target.value }), 0); }}>
                {projectTypes.map((pt) => (
                  <MenuItem key={pt.value} value={pt.value}>{pt.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                options={clients}
                getOptionLabel={(o) => o.name || ''}
                value={clients.find((c) => c.id === formData.client_id) || null}
                onChange={(_, newVal) => { const updated = { ...formData, client_id: newVal?.id || '' }; setFormData(updated); handleSaveGeneral(updated); }}
                renderInput={(params) => <TextField {...params} fullWidth label="Cliente" />}
              />
            </Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Capacidad produccion" name="production_capacity" value={formData.production_capacity} onChange={handleFormChange} onBlur={handleBlurGeneral} /></Grid>
            <Grid item xs={12}><TextField fullWidth multiline rows={3} label="Descripcion del producto" name="product_description" value={formData.product_description} onChange={handleFormChange} onBlur={handleBlurGeneral} /></Grid>
            <Grid item xs={12} sm={3}><TextField fullWidth label="Fecha peticion" type="date" name="request_date" value={formData.request_date} onChange={(e) => { handleFormChange(e); setTimeout(() => handleSaveGeneral({ ...formData, request_date: e.target.value }), 0); }} InputLabelProps={{ shrink: true }} /></Grid>
            <Grid item xs={12} sm={3}><TextField fullWidth label="Fecha oferta" type="date" name="offer_date" value={formData.offer_date} onChange={(e) => { handleFormChange(e); setTimeout(() => handleSaveGeneral({ ...formData, offer_date: e.target.value }), 0); }} InputLabelProps={{ shrink: true }} /></Grid>
            <Grid item xs={12} sm={3}><TextField fullWidth label="Fecha aprobacion" type="date" name="approval_date" value={formData.approval_date} onChange={(e) => { handleFormChange(e); setTimeout(() => handleSaveGeneral({ ...formData, approval_date: e.target.value }), 0); }} InputLabelProps={{ shrink: true }} /></Grid>
            <Grid item xs={12} sm={3}><TextField fullWidth label="Importe Venta (EUR)" type="number" name="approved_amount" value={formData.approved_amount} onChange={handleFormChange} onBlur={handleBlurGeneral} inputProps={{ step: '0.01' }} /></Grid>
            <Grid item xs={12} sm={3}><TextField fullWidth label="Plazo (semanas)" type="number" name="delivery_weeks" value={formData.delivery_weeks} onChange={handleFormChange} onBlur={handleBlurGeneral} /></Grid>
            <Grid item xs={12} sm={3}><TextField fullWidth label="Fecha de entrega" type="date" name="estimated_end_date" value={formData.estimated_end_date} onChange={(e) => { handleFormChange(e); setTimeout(() => handleSaveGeneral({ ...formData, estimated_end_date: e.target.value }), 0); }} InputLabelProps={{ shrink: true }} /></Grid>
            <Grid item xs={12} sm={3}><TextField fullWidth label="Fecha de finalizacion" type="date" name="actual_end_date" value={formData.actual_end_date} onChange={(e) => { handleFormChange(e); setTimeout(() => handleSaveGeneral({ ...formData, actual_end_date: e.target.value }), 0); }} InputLabelProps={{ shrink: true }} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Ruta documentos" value={project.documents_path || ''} InputProps={{ readOnly: true }} /></Grid>
            <Grid item xs={12}><TextField fullWidth multiline rows={2} label="Notas" name="notes" value={formData.notes} onChange={handleFormChange} onBlur={handleBlurGeneral} /></Grid>
          </Grid>
        </Paper>
      </TabPanel>

      <TabPanel value={activeTab} index={1}><ConceptualEngineering projectId={project.id} projectType={project.project_type} /></TabPanel>
      <TabPanel value={activeTab} index={2}><BasicEngineering projectId={project.id} projectType={project.project_type} approvedAmount={project.approved_amount ?? 0} /></TabPanel>
      <TabPanel value={activeTab} index={3}><Offers projectId={project.id} projectType={project.project_type} /></TabPanel>
      <TabPanel value={activeTab} index={4}><DetailEngineering projectId={project.id} projectType={project.project_type} projectDeliveryDate={project.estimated_end_date} approvedAmount={project.approved_amount ?? null} /></TabPanel>

      {/* Fabricación */}
      <TabPanel value={activeTab} index={5}><ManufacturingOrders projectId={project.id} /></TabPanel>

      {/* Planning */}
      <TabPanel value={activeTab} index={6}><ProjectPlanning projectId={project.id} /></TabPanel>

      {/* Documentos */}
      <TabPanel value={activeTab} index={7}>
        <Paper sx={{ p: 3 }}>
          {/* Action bar */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            {!project.documents_path ? (
              <Button
                variant="contained"
                startIcon={creatingFolders ? <CircularProgress size={18} color="inherit" /> : <CreateNewFolderIcon />}
                onClick={handleCreateFolders}
                disabled={creatingFolders}
              >
                Crear estructura de carpetas
              </Button>
            ) : (
              <Button
                variant="outlined"
                size="small"
                startIcon={creatingFolders ? <CircularProgress size={16} color="inherit" /> : <CreateNewFolderIcon />}
                onClick={handleCreateFolders}
                disabled={creatingFolders}
              >
                Regenerar carpetas
              </Button>
            )}
            <Button
              variant="text"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={async () => { const r = await projectsAPI.get(project.id); setProject(r.data); }}
            >
              Actualizar
            </Button>
          </Box>

          {folderError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFolderError(null)}>
              {folderError}
            </Alert>
          )}
          {folderSuccess && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setFolderSuccess(false)}>
              Estructura de carpetas creada en: <strong>{project.documents_path}</strong>
            </Alert>
          )}

          <ProjectFolderLinks project={project} onChange={setProject} />

          {(project.drive_folder_id || project.drive_offer_folder_id || project.documents_path) ? (
            <FileBrowser
              rootPath={project.drive_folder_id || project.drive_offer_folder_id || project.documents_path}
              title="Documentos del proyecto"
            />
          ) : (
            <Typography color="text.secondary">
              Todavía no hay carpetas para este proyecto. Créalas con el botón de arriba o asocia
              una carpeta que ya exista en Drive.
            </Typography>
          )}
        </Paper>
      </TabPanel>

      {/* Condiciones cobro */}
      <TabPanel value={activeTab} index={8}>
        <Paper sx={{ p: 3, mb: 2, backgroundColor: '#e8f5e9' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6" gutterBottom>Condiciones de Cobro del Cliente</Typography>
              <Typography variant="body2" color="textSecondary">
                Condiciones de cobro con <strong>{client?.name || 'cliente'}</strong>.
              </Typography>
            </Box>
            <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={handleCopyClientConditions}>
              Copiar del Cliente
            </Button>
          </Box>
        </Paper>
        <EditableTable title="Condiciones de Cobro" columns={clientCondColumns} rows={clientConds} dialogFields={clientCondFields} {...clientCondHandlers} />
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2">
            Total: <strong>{fmtEUR(clientConds.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0))} EUR</strong> ({clientConds.reduce((s, c) => s + (parseFloat(c.percentage) || 0), 0)}%)
          </Typography>
        </Box>
      </TabPanel>

      {/* Condiciones pago */}
      <TabPanel value={activeTab} index={9}>
        <Paper sx={{ p: 3, mb: 2, backgroundColor: '#fff3e0' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6">Condiciones de Pago a Proveedores</Typography>
              <Typography variant="body2" color="textSecondary">
                Condiciones de pago pactadas con cada proveedor para este proyecto.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Importa proveedores desde la lista de equipos del proyecto y copia sus condiciones por defecto">
                <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefreshSuppliers}>
                  Refrescar
                </Button>
              </Tooltip>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddSupplierOpen(true)}>
                Añadir proveedor
              </Button>
            </Box>
          </Box>
        </Paper>

        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#c62828' }}>
                {[
                  { field: 'supplier_name', label: 'Proveedor', align: 'left' },
                  { field: 'description',   label: 'Descripcion', align: 'left' },
                  { field: 'percentage',    label: '%', align: 'right' },
                  { field: 'milestone',     label: 'Hito', align: 'left' },
                  { field: 'delay_days',    label: 'Dias', align: 'right' },
                  { field: 'amount',        label: 'Importe (EUR)', align: 'right' },
                  { field: 'expected_date', label: 'Fecha Pago', align: 'center' },
                  { field: 'is_paid',       label: 'Pagado', align: 'center' },
                ].map((col) => (
                  <TableCell key={col.field} sx={{ fontWeight: 'bold', color: '#fff' }} align={col.align}
                    sortDirection={scSortField === col.field ? scSortDir : false}>
                    <TableSortLabel
                      active={scSortField === col.field}
                      direction={scSortField === col.field ? scSortDir : 'asc'}
                      onClick={() => {
                        if (scSortField === col.field) setScSortDir((d) => d === 'asc' ? 'desc' : 'asc');
                        else { setScSortField(col.field); setScSortDir('asc'); }
                      }}
                    >
                      {col.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {[...supplierConds].sort((a, b) => {
                const va = a[scSortField] ?? '';
                const vb = b[scSortField] ?? '';
                const cmp = typeof va === 'number' && typeof vb === 'number'
                  ? va - vb
                  : String(va).localeCompare(String(vb), 'es', { numeric: true });
                return scSortDir === 'desc' ? -cmp : cmp;
              }).map((c) => (
                <TableRow
                  key={c.id}
                  hover
                  sx={{ backgroundColor: c.is_paid ? '#e8f5e9' : undefined }}
                >
                  <TableCell>
                    {c.supplier_name}
                    {c.payment_conditions && (
                      <Tooltip title={
                        <Box>
                          <Typography variant="caption" fontWeight="bold">
                            Cond. pago ({c.payment_conditions_source === 'pedido' ? 'del pedido' : 'de la oferta'}):
                          </Typography>
                          <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap' }}>{c.payment_conditions}</Typography>
                        </Box>
                      }>
                        <Chip size="small" label={c.payment_conditions_source === 'pedido' ? 'P' : 'O'}
                          color={c.payment_conditions_source === 'pedido' ? 'primary' : 'warning'}
                          sx={{ ml: 0.5, height: 18, fontSize: '0.65rem', cursor: 'help' }} />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>{c.description}</TableCell>
                  <TableCell align="right">{c.percentage}%</TableCell>
                  <TableCell>{c.milestone}</TableCell>
                  <TableCell align="right">{c.delay_days || 0}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: c.amount > 0 ? 'bold' : undefined }}>
                    {fmtEUR(c.amount)}
                  </TableCell>
                  <TableCell align="center" sx={{ color: c.expected_date ? 'text.primary' : 'text.disabled', fontSize: '0.8rem' }}>
                    {fmtDate(c.expected_date)}
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      size="small"
                      checked={Boolean(c.is_paid)}
                      onChange={() => handleTogglePaid(c)}
                      sx={{ color: c.is_paid ? 'success.main' : undefined, '&.Mui-checked': { color: 'success.main' } }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" color="error" onClick={() => setDeleteConfirmSupplierCond(c.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {supplierConds.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
                      No hay condiciones. Usa "Refrescar" para importar desde equipos o "Añadir proveedor" manualmente.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2">
            Total: <strong>{fmtEUR(supplierConds.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0))} EUR</strong>
            {' '}— Pagado: <strong style={{ color: 'green' }}>{fmtEUR(supplierConds.filter(c => c.is_paid).reduce((s, c) => s + (parseFloat(c.amount) || 0), 0))} EUR</strong>
          </Typography>
        </Box>

        {/* Dialog añadir proveedor */}
        <Dialog open={addSupplierOpen} onClose={() => { setAddSupplierOpen(false); setSelectedSupplier(null); }} maxWidth="sm" fullWidth>
          <DialogTitle>Añadir condiciones de proveedor</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2, mt: 1 }}>
              Selecciona un proveedor. Se copiarán todas sus condiciones de pago por defecto.
            </Typography>
            <Autocomplete
              options={suppliers}
              getOptionLabel={(s) => s.name}
              value={selectedSupplier}
              onChange={(e, val) => setSelectedSupplier(val)}
              renderInput={(params) => (
                <TextField {...params} label="Proveedor" fullWidth placeholder="Buscar proveedor..." />
              )}
              noOptionsText="No hay proveedores registrados"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setAddSupplierOpen(false); setSelectedSupplier(null); }}>Cancelar</Button>
            <Button
              variant="contained"
              disabled={!selectedSupplier || addingSupplier}
              onClick={handleAddSupplierConditions}
            >
              {addingSupplier ? 'Copiando...' : 'Copiar condiciones'}
            </Button>
          </DialogActions>
        </Dialog>
      </TabPanel>

      {/* Tab 8: Flujo de Caja */}
      <TabPanel value={activeTab} index={10}>
        <Paper sx={{ p: 3, mb: 2, backgroundColor: '#c62828', color: '#fff' }}>
          <Typography variant="h6" gutterBottom sx={{ color: '#fff' }}>Flujo de Caja del Proyecto</Typography>
          <Typography variant="body2" sx={{ color: '#fff', opacity: 0.9 }}>
            Cobros calculados desde fechas reales: aprobación (pedido), entrega (entrega/factura), finalización (puesta en marcha), multiplicados por el importe aprobado. Pagos desde fechas previstas de condiciones de proveedores.
          </Typography>
        </Paper>
        <CashFlowProjection
          clientConditions={clientConds}
          supplierConditions={supplierConds}
          approvedAmount={parseFloat(project.approved_amount) || 0}
          approvalDate={project.approval_date}
          deliveryDate={project.estimated_end_date}
          endDate={project.actual_end_date}
        />
      </TabPanel>

      {/* PEDIDOS */}
      <TabPanel value={activeTab} index={11}>
        <Paper sx={{ p: 3, mb: 2, backgroundColor: '#fce4ec' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6">Pedidos de Compra</Typography>
              <Typography variant="body2" color="textSecondary">
                Gestión de pedidos enviados a proveedores. Selecciona una oferta recibida para generar el pedido.
              </Typography>
            </Box>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => openNewOrder()}>
              Nuevo pedido
            </Button>
          </Box>
        </Paper>

        {/* Offers pending an order — grouped by manufacturing order (or loose) */}
        {offersWithoutOrder.length > 0 && (
          <Paper variant="outlined" sx={{ mb: 2, p: 2 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.secondary' }}>
              Ofertas sin pedido — haz clic para generar pedido
            </Typography>
            {groupOffersByManufacturing(offersWithoutOrder).map((group) => (
              <Box key={group.key} sx={{ mb: 1.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold', color: group.code ? 'primary.main' : 'text.secondary' }}>
                  {group.code ? `Orden de fabricación ${group.code}` : 'Ofertas sueltas (sin orden de fabricación)'}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                  {group.offers.map((o) => (
                    <Chip
                      key={o.id}
                      label={`${o.equipment_name} | ${o.supplier_name} — ${fmtEUR(o.amount)} EUR`}
                      variant="outlined"
                      color={group.code ? 'primary' : 'warning'}
                      onClick={() => openNewOrder(o)}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              </Box>
            ))}
          </Paper>
        )}

        {/* Orders table */}
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#c62828' }}>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>N. Pedido</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Orden Fab.</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Proveedor</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }} align="right">Importe (EUR)</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }} align="right">Plazo (sem)</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Fecha Pedido</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Fecha Entrega</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Estado</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', color: '#fff' }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((o) => (
                <TableRow key={o.id} hover>
                  <TableCell><strong>{o.code}</strong></TableCell>
                  <TableCell>
                    {o.manufacturing_order_code
                      ? <Chip size="small" variant="outlined" color="primary" label={o.manufacturing_order_code} />
                      : '—'}
                  </TableCell>
                  <TableCell>{o.supplier_name}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{fmtEUR(o.amount)} EUR</TableCell>
                  <TableCell align="right">{o.delivery_weeks || '—'}</TableCell>
                  <TableCell>{fmtDate(o.order_date)}</TableCell>
                  <TableCell>{fmtDate(o.delivery_date)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={{ draft: 'Borrador', sent: 'Enviado', confirmed: 'Confirmado', delivered: 'Entregado', closed: 'Cerrado' }[o.status] || o.status}
                      color={{ draft: 'default', sent: 'info', confirmed: 'success', delivered: 'warning', closed: 'default' }[o.status] || 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Descargar PDF">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => setPdfLangDialog(o.id)}
                      >
                        <PictureAsPdfIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => openEditOrder(o)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton size="small" color="error" onClick={() => setDeleteConfirmOrder(o.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {orders.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
                      No hay pedidos. Usa el botón "Nuevo pedido" o haz clic en una oferta pendiente.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      </TabPanel>

      {/* Tab 11: Seguimiento (comunicaciones + temas pendientes) */}
      <TabPanel value={activeTab} index={12}>
        <Communications projectId={project.id} />
      </TabPanel>

      {/* Tab 13: Chat del proyecto (el mismo que ven tecnicos y gestores en la app movil) */}
      <TabPanel value={activeTab} index={13}>
        <Paper sx={{ height: 'calc(100vh - 330px)', minHeight: 420, overflow: 'hidden' }}>
          <ProjectChat projectId={project.id} />
        </Paper>
      </TabPanel>

      {/* Dialog: crear/editar pedido */}
      <Dialog open={orderDialogOpen} onClose={() => setOrderDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{orderEditId ? 'Editar Pedido' : 'Nuevo Pedido de Compra'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={suppliers}
                getOptionLabel={(s) => (typeof s === 'string' ? s : s.name)}
                value={suppliers.find((s) => s.id === orderForm.supplier_id) || null}
                onChange={async (e, val) => {
                  const newForm = { ...orderForm, supplier_id: val?.id || '', supplier_name: val?.name || '' };
                  if (val?.id && !orderEditId && !newForm.payment_conditions) {
                    newForm.payment_conditions = await fetchSupplierPaymentConditions(val.id);
                  }
                  setOrderForm(newForm);
                }}
                renderInput={(params) => <TextField {...params} label="Proveedor *" fullWidth />}
                noOptionsText="No hay proveedores"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select fullWidth label="Oferta asociada"
                value={orderForm.supplier_offer_id || ''}
                onChange={(e) => {
                  const offerId = e.target.value;
                  const offer = offersWithoutOrder.find((o) => o.id === offerId);
                  setOrderForm({
                    ...orderForm,
                    supplier_offer_id: offerId,
                    amount: offer ? offer.amount : orderForm.amount,
                    delivery_weeks: offer ? offer.delivery_weeks : orderForm.delivery_weeks,
                    supplier_id: offer ? offer.supplier_id : orderForm.supplier_id,
                    supplier_name: offer ? offer.supplier_name : orderForm.supplier_name,
                    manufacturing_order_id: offer ? offer.manufacturing_order_id : null,
                    manufacturing_order_code: offer ? offer.manufacturing_order_code : null,
                    comments: offer?.notes || orderForm.comments,
                  });
                }}
              >
                <MenuItem value=""><em>Sin oferta asociada</em></MenuItem>
                {groupOffersByManufacturing(offersWithoutOrder).flatMap((group) => [
                  <ListSubheader key={`h-${group.key}`}>
                    {group.code ? `Orden de fabricación ${group.code}` : 'Ofertas sueltas'}
                  </ListSubheader>,
                  ...group.offers.map((o) => (
                    <MenuItem key={o.id} value={o.id}>
                      {o.equipment_name} — {o.supplier_name} ({fmtEUR(o.amount)} EUR)
                    </MenuItem>
                  )),
                ])}
              </TextField>
              {orderForm.manufacturing_order_code && (
                <Chip
                  size="small" color="primary" variant="outlined" sx={{ mt: 1 }}
                  label={`Orden de fabricación: ${orderForm.manufacturing_order_code}`}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth label="Importe (EUR)" type="number"
                value={orderForm.amount || ''}
                onChange={(e) => setOrderForm({ ...orderForm, amount: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth label="Plazo entrega (semanas)" type="number"
                value={orderForm.delivery_weeks || ''}
                onChange={(e) => {
                  const weeks = parseInt(e.target.value) || 0;
                  const updated = { ...orderForm, delivery_weeks: e.target.value };
                  if (weeks > 0 && orderForm.order_date) {
                    const d = new Date(orderForm.order_date);
                    d.setDate(d.getDate() + weeks * 7);
                    updated.delivery_date = d.toISOString().slice(0, 10);
                  }
                  setOrderForm(updated);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth label="Fecha del pedido" type="date"
                value={orderForm.order_date || ''}
                onChange={(e) => {
                  const updated = { ...orderForm, order_date: e.target.value };
                  const weeks = parseInt(orderForm.delivery_weeks) || 0;
                  if (weeks > 0 && e.target.value) {
                    const d = new Date(e.target.value);
                    d.setDate(d.getDate() + weeks * 7);
                    updated.delivery_date = d.toISOString().slice(0, 10);
                  }
                  setOrderForm(updated);
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth label="Fecha de entrega" type="date"
                value={orderForm.delivery_date || ''}
                onChange={(e) => setOrderForm({ ...orderForm, delivery_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth select label="Estado"
                value={orderForm.status || 'draft'}
                onChange={(e) => setOrderForm({ ...orderForm, status: e.target.value })}
              >
                <MenuItem value="draft">Borrador</MenuItem>
                <MenuItem value="sent">Enviado</MenuItem>
                <MenuItem value="confirmed">Confirmado</MenuItem>
                <MenuItem value="delivered">Entregado</MenuItem>
                <MenuItem value="closed">Cerrado</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5, fontWeight: 'bold' }}>
                Condiciones de pago
              </Typography>
              {orderEditId ? (
                <Paper variant="outlined" sx={{ p: 1 }}>
                  <PaymentConditionsEditor
                    conditions={orderPaymentConds}
                    onAdd={async (data) => {
                      await ordersAPI.createPaymentCondition(orderEditId, data);
                      await loadOrderPaymentConds(orderEditId);
                      await reloadOrders();
                    }}
                    onUpdate={async (id, data) => {
                      await ordersAPI.updatePaymentCondition(id, data);
                      await loadOrderPaymentConds(orderEditId);
                      await reloadOrders();
                    }}
                    onDelete={async (id) => {
                      await ordersAPI.deletePaymentCondition(id);
                      await loadOrderPaymentConds(orderEditId);
                      await reloadOrders();
                    }}
                    amount={parseFloat(orderForm.amount) || 0}
                  />
                </Paper>
              ) : (
                <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                  Las condiciones de pago se copian automaticamente del proveedor al crear el pedido. Podras editarlas despues.
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth multiline rows={6} label="Condiciones legales del pedido"
                value={orderForm.legal_terms || ''}
                onChange={(e) => setOrderForm({ ...orderForm, legal_terms: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth multiline rows={3} label="Comentarios / Particularidades"
                value={orderForm.comments || ''}
                onChange={(e) => setOrderForm({ ...orderForm, comments: e.target.value })}
                placeholder="Indicar condiciones o particularidades específicas de este pedido..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrderDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" disabled={orderSaving || !orderForm.supplier_id} onClick={handleSaveOrder}>
            {orderSaving ? 'Guardando...' : 'Guardar pedido'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo confirmación avance a Ing. Detalle */}
      <Dialog open={confirmAdvanceOpen} onClose={() => setConfirmAdvanceOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>⚠️ Confirmar paso a Ingeniería de Detalle</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Estás a punto de pasar el proyecto a <strong>Ingeniería de Detalle (En Ejecución)</strong>.
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Este es un paso <strong>crítico e irreversible</strong> que implicará:
          </Typography>
          <Box component="ul" sx={{ mt: 1, color: 'text.secondary' }}>
            <li>Asignación automática de un número de proyecto</li>
            <li>Inicio oficial de la fase de ingeniería de detalle</li>
            <li>Compromiso formal con el cliente</li>
          </Box>
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold', color: 'warning.main' }}>
            ¿Confirmas que la oferta ha sido aprobada y deseas iniciar la ejecución?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAdvanceOpen(false)} color="inherit">Cancelar</Button>
          <Button variant="contained" color="success" onClick={doAdvancePhase}>
            Sí, iniciar Ing. Detalle
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm delete supplier condition */}
      <Dialog open={deleteConfirmSupplierCond !== null} onClose={() => setDeleteConfirmSupplierCond(null)} maxWidth="xs">
        <DialogTitle>Confirmar eliminacion</DialogTitle>
        <DialogContent>
          <Typography>¿Estas seguro de que deseas eliminar esta condicion de proveedor?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmSupplierCond(null)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={async () => { await handleDeleteSupplierCond(deleteConfirmSupplierCond); setDeleteConfirmSupplierCond(null); }}>Eliminar</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm delete order */}
      <Dialog open={deleteConfirmOrder !== null} onClose={() => setDeleteConfirmOrder(null)} maxWidth="xs">
        <DialogTitle>Confirmar eliminacion</DialogTitle>
        <DialogContent>
          <Typography>¿Estas seguro de que deseas eliminar este pedido?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOrder(null)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={async () => { await handleDeleteOrder(deleteConfirmOrder); setDeleteConfirmOrder(null); }}>Eliminar</Button>
        </DialogActions>
      </Dialog>

      {/* PDF language selection */}
      <Dialog open={pdfLangDialog !== null} onClose={() => setPdfLangDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Idioma del PDF / PDF Language</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>Selecciona el idioma del pedido:</Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              fullWidth
              onClick={() => handleDownloadOrderPdf(pdfLangDialog, 'es')}
            >
              Español
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => handleDownloadOrderPdf(pdfLangDialog, 'en')}
            >
              English
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPdfLangDialog(null)}>Cancelar</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}


function CashFlowProjection({ clientConditions, supplierConditions, approvedAmount, approvalDate, deliveryDate, endDate }) {
  const apprDate = approvalDate ? new Date(approvalDate) : null;
  const delivDate = deliveryDate ? new Date(deliveryDate) : null;
  const finDate = endDate ? new Date(endDate) : null;

  const transactions = [];

  if (apprDate) {
    clientConditions.forEach(c => {
      const pct = parseFloat(c.percentage) || 0;
      const amt = (approvedAmount || 0) * pct / 100;
      if (!amt) return;
      const delay = parseInt(c.delay_days) || 0;
      let baseDate = null;
      if (c.milestone === 'pedido') {
        baseDate = new Date(apprDate);
        baseDate.setDate(baseDate.getDate() + delay);
      } else if (c.milestone === 'puesta_en_marcha') {
        if (!finDate) return; // skip if no end date set
        baseDate = new Date(finDate);
        baseDate.setDate(baseDate.getDate() + delay);
      } else {
        // entrega, factura, etc.
        if (!delivDate) return; // skip if no delivery date set
        baseDate = new Date(delivDate);
        baseDate.setDate(baseDate.getDate() + delay);
      }
      if (baseDate) transactions.push({ type: 'income', date: baseDate, amount: amt, label: c.milestone });
    });
  }

  supplierConditions.forEach(c => {
    if (!c.expected_date) return;
    const amt = parseFloat(c.amount) || 0;
    if (!amt) return;
    transactions.push({ type: 'expense', date: new Date(c.expected_date), amount: amt, label: c.milestone });
  });

  const missingDates = [];
  if (!apprDate) missingDates.push('fecha de aprobación e importe aprobado');
  else if (!delivDate) missingDates.push('fecha de entrega');

  if (transactions.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="textSecondary">
          {missingDates.length > 0
            ? `Introduce ${missingDates.join(' y ')} en Datos Generales para calcular el flujo de caja.`
            : 'No hay condiciones de pago configuradas o no tienen fechas previstas aún.'}
        </Typography>
      </Paper>
    );
  }

  // Build month buckets spanning all transaction dates
  const allDates = transactions.map(t => t.date.getTime());
  const firstMonth = new Date(Math.min(...allDates));
  const lastMonth = new Date(Math.max(...allDates));
  firstMonth.setDate(1);
  lastMonth.setDate(1);

  const periods = [];
  const cur = new Date(firstMonth);
  while (cur <= lastMonth) {
    periods.push(`${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}`);
    cur.setMonth(cur.getMonth() + 1);
  }

  let accumulated = 0;
  const rows = periods.map(period => {
    let income = 0, expense = 0;
    transactions.forEach(t => {
      const tp = `${t.date.getFullYear()}-${String(t.date.getMonth() + 1).padStart(2, '0')}`;
      if (tp === period) {
        if (t.type === 'income') income += t.amount;
        else expense += t.amount;
      }
    });
    const balance = income - expense;
    accumulated += balance;
    return { period, income, expense, balance, accumulated };
  });

  const totalIncome = transactions.filter(t => t.type === 'income').reduce((s, t) => s + t.amount, 0);
  const totalExpense = transactions.filter(t => t.type === 'expense').reduce((s, t) => s + t.amount, 0);

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="textSecondary">Importe aprobado</Typography>
            <Typography variant="h6" fontWeight="bold">{fmtEUR(approvedAmount || 0)} EUR</Typography>
          </Paper>
        </Grid>
        <Grid item xs={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="textSecondary">Total Cobros</Typography>
            <Typography variant="h6" color="success.main" fontWeight="bold">
              {fmtEUR(totalIncome)} EUR
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="textSecondary">Total Pagos</Typography>
            <Typography variant="h6" color="error.main" fontWeight="bold">
              {fmtEUR(totalExpense)} EUR
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="textSecondary">Margen</Typography>
            <Typography variant="h6" color={totalIncome - totalExpense >= 0 ? 'success.main' : 'error.main'} fontWeight="bold">
              {fmtEUR(totalIncome - totalExpense)} EUR
            </Typography>
          </Paper>
        </Grid>
      </Grid>
      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#c62828' }}>
              <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Periodo</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#fff' }} align="right">Cobros</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#fff' }} align="right">Pagos</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#fff' }} align="right">Balance</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#fff' }} align="right">Acumulado</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(m => (
              <TableRow key={m.period} hover>
                <TableCell><strong>{m.period}</strong></TableCell>
                <TableCell align="right" sx={{ color: m.income > 0 ? 'green' : undefined }}>{m.income > 0 ? `+${fmtEUR(m.income)}` : '-'}</TableCell>
                <TableCell align="right" sx={{ color: m.expense > 0 ? 'red' : undefined }}>{m.expense > 0 ? `-${fmtEUR(m.expense)}` : '-'}</TableCell>
                <TableCell align="right" sx={{ color: m.balance >= 0 ? 'green' : 'red', fontWeight: 'bold' }}>{fmtEUR(m.balance)}</TableCell>
                <TableCell align="right" sx={{ color: m.accumulated >= 0 ? 'green' : 'red', fontWeight: 'bold' }}>{fmtEUR(m.accumulated)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
